import { useSyncExternalStore } from "react";

/**
 * Notification-sound layer: the chime that plays when an attention toast or
 * the ambient attention surface appears. Deliberately independent of the
 * interaction-sound store (`lib/sound.ts`): muting tap and toggle cues does
 * not silence notification chimes, and muting chimes does not silence cues.
 *
 * Same shape as that store: a module store (no provider), a localStorage copy
 * for this device, and a server copy on the onboarding profile
 * (`notificationSound`) because the desktop sidecar renders the app in a
 * cross-origin iframe whose localStorage does not survive a relaunch.
 *
 * Unlike interaction sounds (cuelume synthesis), the chime is a single asset
 * played through one shared HTMLAudioElement owned here, so the mute has
 * exactly one seam instead of one per call site.
 */

const STORAGE_KEY = "healthos-notification-sound";
const CHIME_SRC = "/sounds/chime.mp3";
const CHIME_VOLUME = 0.5;

function readPref(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "0") return false;
    if (v === "1") return true;
  } catch {
    // localStorage unavailable; default on.
  }
  return true;
}

function hasStoredPref(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "0" || v === "1";
  } catch {
    return false;
  }
}

function writePref(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
  } catch {
    // localStorage unavailable; the preference just won't persist.
  }
}

let enabled = readPref();
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export function isNotificationSoundEnabled(): boolean {
  return enabled;
}

/**
 * Adopt the server-persisted preference (onboarding profile
 * `notificationSound`) when this browser holds no explicit choice of its own.
 * A "0"/"1" already in localStorage means the member chose on this device,
 * and that choice was itself patched to the profile, so it wins for the
 * session. Deliberately does not write localStorage: the key keeps meaning
 * "an explicit choice made here".
 */
export function hydrateNotificationSoundPref(serverOn: boolean | null | undefined): void {
  if (typeof serverOn !== "boolean") return;
  if (hasStoredPref()) return;
  if (serverOn === enabled) return;
  enabled = serverOn;
  emit();
}

export function setNotificationSoundEnabled(on: boolean): void {
  if (on === enabled) return;
  enabled = on;
  writePref(on);
  emit();
}

let chime: HTMLAudioElement | null = null;
let chimeUnavailable = false;

function ensureChime(): HTMLAudioElement | null {
  if (chime || chimeUnavailable) return chime;
  try {
    const audio = new Audio(CHIME_SRC);
    audio.preload = "auto";
    audio.volume = CHIME_VOLUME;
    chime = audio;
  } catch {
    // Audio constructor unsupported; the chime just never plays.
    chimeUnavailable = true;
  }
  return chime;
}

/** Construct and preload the chime ahead of first use, so the first
 *  notification does not wait on a network fetch. Safe to call repeatedly. */
export function primeNotificationChime(): void {
  ensureChime();
}

/** Play the notification chime. No-op when muted, when Audio is unavailable,
 *  or when autoplay is blocked; the visual reveal still lands either way. */
export function playNotificationChime(): void {
  if (!enabled) return;
  const audio = ensureChime();
  if (!audio) return;
  try {
    audio.currentTime = 0;
    void audio.play().catch(() => {
      /* autoplay blocked */
    });
  } catch {
    // A failed sound must never surface to the user.
  }
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Subscribe to enabled-state changes. Exposed for tests; components use
 *  `useNotificationSound`. */
export const subscribeNotificationSound = subscribe;

export interface NotificationSoundControls {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  play: () => void;
}

/** Reactive access to the notification-sound state. No provider required. */
export function useNotificationSound(): NotificationSoundControls {
  const value = useSyncExternalStore(
    subscribe,
    isNotificationSoundEnabled,
    isNotificationSoundEnabled,
  );
  return { enabled: value, setEnabled: setNotificationSoundEnabled, play: playNotificationChime };
}
